/* `/agent_policies/rules` — an agent's rules (the screen's "policies"): who they match and
   what happens to them (`backend/modules/agent_policies/README.md`). Writes apply at once.
   Only the agents feature reads them, so they live in this feature, not `shared/api`
   (rule book §1 rule 3).

   Every write changes who lands where, so each one re-reads the list (its `matches`) and any
   check a tester holds open: both sit under `ruleKeys.all`.

   Paths are bare; `client.baseURL` carries `/api/v1`. Every enveloped answer goes through
   `unwrap` — DELETE is the one route with no envelope, a 204. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "@/shared/api/client";
import { unwrap, type Envelope } from "@/shared/api/envelope";

import type { Rule, RuleCheck, RuleCheckBody, RuleCreate, RuleList, RulePatch } from "../types";

/** A 51st rule (409, `TooManyRules`). */
export const TOO_MANY_RULES = 3260015;
/** An order that doesn't name each of the agent's rules once (409, `RuleOrderStale`): another
    tab changed them. */
export const RULE_ORDER_STALE = 3260016;

export const ruleKeys = {
  all: ["policy-rules"] as const,
  list: (agentId: string) => [...ruleKeys.all, "list", agentId] as const,
  check: (body: RuleCheckBody) => [...ruleKeys.all, "check", body] as const,
};

/* ------------------------------ endpoints -------------------------------- */

export async function listRules(agentId: string): Promise<RuleList> {
  return unwrap(await client.get<Envelope<RuleList>>("/agent_policies/rules", { params: { agent_id: agentId } }));
}

/** 201; last without a `position`. Past 50 is `TOO_MANY_RULES`. */
export async function createRule(body: RuleCreate): Promise<Rule> {
  return unwrap(await client.post<Envelope<Rule>>("/agent_policies/rules", body));
}

/** Only the fields sent change; a null budget, limit or message clears it. */
export async function patchRule(id: string, body: RulePatch): Promise<Rule> {
  return unwrap(await client.patch<Envelope<Rule>>(`/agent_policies/rules/${id}`, body));
}

/** 204; the ones after it move up. */
export async function deleteRule(id: string): Promise<void> {
  await client.delete(`/agent_policies/rules/${id}`);
}

/** Each of the agent's rules once, in the order they are tried. */
export async function orderRules(agentId: string, ids: string[]): Promise<RuleList> {
  return unwrap(await client.put<Envelope<RuleList>>("/agent_policies/rules/order", { agent_id: agentId, ids }));
}

/** The rule someone meets first (null: Everyone else) and why. */
export async function checkRule(body: RuleCheckBody): Promise<RuleCheck> {
  return unwrap(await client.post<Envelope<RuleCheck>>("/agent_policies/rules/check", body));
}

/* -------------------------------- hooks ---------------------------------- */

export function useRules(agentId: string) {
  return useQuery({ queryKey: ruleKeys.list(agentId), queryFn: () => listRules(agentId) });
}

/** Who to check, or null while there is nobody to. */
export function useRuleCheck(body: RuleCheckBody | null) {
  return useQuery({
    queryKey: ruleKeys.check(body ?? { agent_id: "" }),
    queryFn: () => checkRule(body!),
    enabled: !!body,
  });
}

/* Whatever the answer, the list is read again: a refused write leaves the screen holding the
   edit the server turned down, and re-reading puts the real rule back. */
function useRulesMutation<V, T>(
  mutationFn: (vars: V) => Promise<T>,
  options: { onSuccess?: (data: T, vars: V) => void; silent?: boolean } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: options.onSuccess,
    onSettled: () => qc.invalidateQueries({ queryKey: ruleKeys.all }),
    meta: options.silent ? { silent: true } : undefined,
  });
}

/* Silent: the section words a 51st rule itself. The new rule goes in at once, so the editor
   can open it before the re-read. */
export function useCreateRule() {
  const qc = useQueryClient();
  return useRulesMutation(createRule, {
    silent: true,
    onSuccess: (rule) => qc.setQueryData<RuleList>(ruleKeys.list(rule.agent_id), (list) => {
      if (!list) return list;
      const rules = list.rules.filter((r) => r.id !== rule.id);
      rules.splice(rule.position, 0, rule);
      return { ...list, rules };
    }),
  });
}

/* The answer replaces the rule at once, so a switch or a name doesn't wait on the re-read. */
export function usePatchRule(agentId: string) {
  const qc = useQueryClient();
  return useRulesMutation(
    (v: { id: string; body: RulePatch }) => patchRule(v.id, v.body),
    {
      onSuccess: (rule) => qc.setQueryData<RuleList>(ruleKeys.list(agentId), (list) =>
        list && { ...list, rules: list.rules.map((r) => (r.id === rule.id ? rule : r)) }),
    },
  );
}

export const useDeleteRule = () => useRulesMutation(deleteRule);

/* Optimistic: the cards stay where they were dropped (or moved from the menu) while the
   order is sent. */
export function useOrderRules(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => orderRules(agentId, ids),
    onMutate: (ids) => {
      void qc.cancelQueries({ queryKey: ruleKeys.list(agentId) });
      qc.setQueryData<RuleList>(ruleKeys.list(agentId), (list) => {
        if (!list) return list;
        const byId = new Map(list.rules.map((r) => [r.id, r]));
        const rules = ids.map((id) => byId.get(id)).filter((r): r is Rule => !!r);
        return { ...list, rules: rules.map((r, position) => ({ ...r, position })) };
      });
    },
    onSuccess: (list) => qc.setQueryData(ruleKeys.list(agentId), list),
    onSettled: () => qc.invalidateQueries({ queryKey: ruleKeys.all }),
  });
}
